import { SharedMarketType } from '@shared/types'
import { SECONDS_IN_A_DAY } from '@shared/utils/constant'
import { deprecatedMarkets } from '@/app/data/market'
import { TradeSubPage } from '@/types'
import type { SharedUiMarketHistory } from '@shared/types'
import type { PriceLevel, ExpiryFuturesMarket } from '@injectivelabs/sdk-ts'
import type { MarketRoute, UiMarketWithToken } from '@/types'

interface PriceLevelMap {
  [price: string]: PriceLevel
}

export const getMarketRoute = (market: UiMarketWithToken): MarketRoute => {
  if (deprecatedMarkets.map((m) => m.slug).includes(market.slug)) {
    return {
      name: TradeSubPage.Market,
      params: {
        market: market.slug
      }
    }
  }

  if (market.type === SharedMarketType.Derivative) {
    if (
      [SharedMarketType.Futures, SharedMarketType.Perpetual].includes(
        market.subType
      )
    ) {
      return {
        name: TradeSubPage.Futures,
        params: {
          slug: market.slug
        }
      }
    }

    /* Default derivative market route */
    return {
      name: TradeSubPage.Derivatives,
      params: {
        derivative: market.slug
      }
    }
  }

  if (market.type === SharedMarketType.Spot) {
    return {
      name: TradeSubPage.Spot,
      params: {
        slug: market.slug
      }
    }
  }

  return {
    name: TradeSubPage.Market,
    params: {
      market: market.slug
    }
  }
}

export const getFormattedMarketsHistoryChartData = (
  marketsHistory: SharedUiMarketHistory
) => {
  return marketsHistory.time.map((time, index, times) => {
    const openPrice = marketsHistory.openPrice[index] || 0
    const highPrice = marketsHistory.highPrice[index] || 0
    const lowPrice = marketsHistory.lowPrice[index] || 0
    const closePrice = marketsHistory.closePrice[index] || 0
    const totalPrice = openPrice + highPrice + lowPrice + closePrice

    const yAxisHolcAveragePrice = toBigNumber(totalPrice)
      .dividedBy(4)
      .toNumber()

    const xAxisTime = time - (times[0] || 0)

    return [xAxisTime, yAxisHolcAveragePrice]
  })
}

export const marketHasRecentlyExpired = (market: ExpiryFuturesMarket) => {
  const now = Date.now() / 1000
  const secondsInADay = SECONDS_IN_A_DAY.toNumber()

  if (!market) {
    return false
  }

  if (!market.expiryFuturesMarketInfo) {
    return false
  }

  if (!market.expiryFuturesMarketInfo.expirationTimestamp) {
    return false
  }

  const isExpired = market.expiryFuturesMarketInfo.expirationTimestamp <= now

  if (!isExpired) {
    return false
  }

  return (
    market.expiryFuturesMarketInfo.expirationTimestamp + secondsInADay * 7 > now
  )
}

/**
 * 1. if new exists in current, update quantity in current,
 * 2. if new exists in current and quantity is 0, delete from current
 * 3. If new doesn't exist in current, add to current
 **/
export const updateOrderbookRecord = (
  currentRecords: PriceLevel[] = [],
  updatedRecords: PriceLevel[] = []
) => {
  const currentRecordsMap: PriceLevelMap = currentRecords.reduce(
    (currentRecordsMap, record) => {
      currentRecordsMap[record.price] = record

      return currentRecordsMap
    },
    {} as PriceLevelMap
  )

  updatedRecords.forEach((record) => {
    currentRecordsMap[record.price] = record
  })

  return Object.values(currentRecordsMap).filter((record) =>
    toBigNumber(record.quantity).gt(0)
  )
}

export const combineOrderbookRecords = ({
  isBuy,
  updatedRecords = [],
  currentRecords = []
}: {
  isBuy: boolean
  updatedRecords?: PriceLevel[]
  currentRecords?: PriceLevel[]
}) => {
  const combinedOrderbookRecords = updateOrderbookRecord(
    currentRecords,
    updatedRecords
  )

  return combinedOrderbookRecords.sort((a, b) => {
    return isBuy
      ? toBigNumber(b.price).minus(a.price).toNumber()
      : toBigNumber(a.price).minus(b.price).toNumber()
  })
}
